/**
 * Shared D3 bubble-graph rendering primitives (bd deepcontext-ulrb, flat
 * restyle bd deepcontext-71j5).
 *
 * Ported from the approved design_html reference demo: flat matte per-series
 * fills with a faint top-light sheen (no 3D glossy sphere, no darkened rim),
 * a shared soft drop-shadow, radius-scaled labels that drop into a floating
 * pill below small bubbles, a dashed selection ring, and a segmented
 * sentiment ring hugging the bubble edge. One module so ClustersGraph and the
 * Top Mentions bubble graph draw the identical restyled bubble instead of two
 * divergent implementations.
 *
 * Deliberately hand-rolled colour blending and arc-path math instead of
 * pulling in d3-color / d3-shape: those ship ESM-only and this repo's Jest
 * leaves node_modules untransformed (see jest.config.js), so a real import
 * would break every test that mounts a bubble graph without adding a new
 * mock. The blend/arc math here doesn't need to be colour-science-exact —
 * only close enough to read as the same depth/ring the demo showed.
 */
import React from 'react';
import {ChartPalette} from './chartTheme';

export interface BubbleSentimentBreakdown {
    positive: number;
    neutral: number;
    negative: number;
}

export interface BubbleSentimentColors {
    positive: string;
    neutral: string;
    negative: string;
}

// Ported 1:1 from the locked design_html reference.
/** How far the top-light sheen (`.bub::before` in design_html) lightens the
 * flat fill at the very top of the bubble before fading out by 45% height. */
const TOP_SHEEN_AMOUNT = 0.22;
const TOP_SHEEN_FADE_OFFSET = '45%';
const SENTIMENT_GAP_RAD = 0.03;
const SENTIMENT_RING_INNER_OFFSET = 3.5;
/** Exported so d3-force collide radii can be sized to clear the ring (bd deepcontext-6l96.2). */
export const SENTIMENT_RING_OUTER_OFFSET = 7;
const SELECTION_RING_OFFSET = 13;
/** Collide clearance beyond the ring's own outer offset (bd deepcontext-6l96.2) — a
 * bare `r + SENTIMENT_RING_OUTER_OFFSET` collide radius lets two rings settle
 * exactly tangent; this margin keeps them provably apart at rest. */
const COLLIDE_RING_MARGIN = 2;
/** Below this radius a label can't fit legibly inside the bubble, so it
 * drops into a floating pill below the bubble instead. Matches the
 * design_html demo's `r > 28` split. */
const LABEL_OUTSIDE_THRESHOLD = 28;
/** Pill geometry/typography (design_html `.pill`). */
const PILL_GAP = 14;
const PILL_HEIGHT = 20;
const PILL_PADDING_X = 8;
const PILL_FONT_SIZE = 11;
const PILL_LABEL_GAP = 4;
const PILL_BG_OPACITY = 0.92;
/** Rough per-character widths at 11px Plus Jakarta Sans — good enough for a
 * pill that only needs to fit its own text, not pixel-exact typesetting. */
const PILL_CHAR_WIDTH_LABEL = 6.2;
const PILL_CHAR_WIDTH_SUBLABEL = 5.6;

function hexToRgb(hex: string): [number, number, number] {
    const stripped = hex.replace('#', '');
    const full =
        stripped.length === 3
            ? stripped
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : stripped;
    const num = parseInt(full, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
    return `#${[r, g, b]
        .map((v) => clamp(v).toString(16).padStart(2, '0'))
        .join('')}`;
}

/** Blend a hex colour toward white — a lightweight stand-in for d3.color().brighter(). */
function lighten(hex: string, amount: number): string {
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(
        r + (255 - r) * amount,
        g + (255 - g) * amount,
        b + (255 - b) * amount,
    );
}

/** Hex + alpha as an rgba() string, for the pill's translucent surface fill. */
function hexToRgba(hex: string, alpha: number): string {
    const [r, g, b] = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function bubbleGradientId(idPrefix: string, paletteIndex: number): string {
    return `${idPrefix}-grad-${paletteIndex}`;
}

/**
 * Shadow filter region, in user space (each bubble is drawn at its own local
 * origin): biggest bubble radius (ClustersGraph MAX_R 90) + blur reach (~27),
 * with extra room below for the dy offset. A percentage region would clip the
 * shadow of small bubbles into a visible square.
 */
const SHADOW_REGION_HALF = 130;
const SHADOW_REGION_DROP = 10;

export function bubbleShadowFilterId(idPrefix: string): string {
    return `${idPrefix}-shadow`;
}

export function bubbleFill(idPrefix: string, paletteIndex: number): string {
    return `url(#${bubbleGradientId(idPrefix, paletteIndex)})`;
}

export function bubbleShadow(idPrefix: string): string {
    return `url(#${bubbleShadowFilterId(idPrefix)})`;
}

export interface BubbleDefsProps {
    /** Namespaces gradient/filter ids so multiple bubble graphs can mount on one page. */
    idPrefix: string;
    palette: ChartPalette;
}

/**
 * One flat two-stop linearGradient per categorical palette slot — a faint
 * top-light sheen (lightened +0.22) fading to the plain fill colour by 45%
 * height, top to bottom — plus the shared soft-shadow filter (feDropShadow
 * dy 6, blur 7, opacity .22 light / .45 dark). No radial highlight, no
 * darkened rim: this is a flat matte bubble, not a glossy sphere. Render
 * once per <svg>, before any <BubbleCircle>.
 */
export const BubbleDefs: React.FC<BubbleDefsProps> = ({idPrefix, palette}) => (
    <defs>
        {palette.categorical.map((hex, i) => (
            <linearGradient key={hex + i} id={bubbleGradientId(idPrefix, i)} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lighten(hex, TOP_SHEEN_AMOUNT)} />
                <stop offset={TOP_SHEEN_FADE_OFFSET} stopColor={hex} />
            </linearGradient>
        ))}
        <filter
            id={bubbleShadowFilterId(idPrefix)}
            filterUnits="userSpaceOnUse"
            x={-SHADOW_REGION_HALF}
            y={-SHADOW_REGION_HALF}
            width={SHADOW_REGION_HALF * 2}
            height={SHADOW_REGION_HALF * 2 + SHADOW_REGION_DROP}
        >
            <feDropShadow
                dx={0}
                dy={6}
                stdDeviation={7}
                floodColor="#000"
                floodOpacity={palette.mode === 'light' ? 0.22 : 0.45}
            />
        </filter>
    </defs>
);

export interface BubbleCircleProps {
    r: number;
    /** Index into palette.categorical — which gradient this bubble fills with. */
    paletteIndex: number;
    idPrefix: string;
}

/** The bubble itself: flat fill + top sheen (one gradient), soft shadow. No stroke — design_html's flat bubble has none. */
export const BubbleCircle: React.FC<BubbleCircleProps> = ({r, paletteIndex, idPrefix}) => (
    <circle r={r} fill={bubbleFill(idPrefix, paletteIndex)} filter={bubbleShadow(idPrefix)} />
);

export interface BubbleSelectionRingProps {
    r: number;
    stroke: string;
}

/** Dashed selection ring, hugging the bubble at r+13. */
export const BubbleSelectionRing: React.FC<BubbleSelectionRingProps> = ({r, stroke}) => (
    <circle
        r={r + SELECTION_RING_OFFSET}
        fill="none"
        stroke={stroke}
        strokeOpacity={0.55}
        strokeDasharray="5 5"
    />
);

export interface BubbleLabelProps {
    r: number;
    label: string;
    subLabel?: string;
    /** Theme chrome — ink/inkMuted/gridline/surface all feed the below-threshold pill. */
    palette: ChartPalette;
    /**
     * Opt-in: above the radius threshold, also drop to the pill if the label
     * is estimated too wide to fit the bubble legibly at the computed font
     * size, instead of clipping. Default false keeps the radius-only split
     * (ClustersGraph's locked labels stay byte-for-byte unchanged).
     */
    fitLabel?: boolean;
    /**
     * bd deepcontext-yz31.3: at high bubble density a large share of bubbles
     * fall below LABEL_OUTSIDE_THRESHOLD, and their pills overlap into an
     * unreadable mass. When true, suppresses the below-bubble pill entirely
     * (the caller re-shows it on hover/selection instead). Has no effect on
     * an in-fill label. Default false — unchanged behaviour everywhere else.
     */
    pillHidden?: boolean;
}

/** design_html `.pill`'s own width estimate — just enough to fit its text, not exact typesetting. */
function pillWidth(label: string, subLabel?: string): number {
    const labelWidth = label.length * PILL_CHAR_WIDTH_LABEL;
    const subLabelWidth = subLabel != null ? PILL_LABEL_GAP + subLabel.length * PILL_CHAR_WIDTH_SUBLABEL : 0;
    return labelWidth + subLabelWidth + PILL_PADDING_X * 2;
}

/** Estimated in-bubble label width at a given font size, reusing the pill's
 * own per-character estimate scaled off the font size it was calibrated at. */
function estimatedInFillLabelWidth(label: string, fontSize: number): number {
    return label.length * PILL_CHAR_WIDTH_LABEL * (fontSize / PILL_FONT_SIZE);
}

/** Shared by `BubbleLabel` and `bubbleCollideRadius` — whether this bubble's
 * label drops to the below-bubble pill instead of rendering in-fill. */
function labelIsOutside(r: number, label: string, fitLabel: boolean): boolean {
    const labelFontSize = Math.max(9, Math.min(13, r / 3.6));
    const doesNotFit = fitLabel && estimatedInFillLabelWidth(label, labelFontSize) > r * 2;
    return r <= LABEL_OUTSIDE_THRESHOLD || doesNotFit;
}

/**
 * How large a d3-force `forceCollide` radius this bubble needs so its
 * sentiment ring (bd deepcontext-6l96.2) — and, when its label drops into the
 * below-bubble pill, that pill too — never overlaps a neighbouring bubble.
 * Pass the same `label`/`subLabel`/`fitLabel` given to `BubbleLabel` so the
 * pill-vs-in-fill placement this returns matches what actually renders.
 */
export function bubbleCollideRadius(
    r: number,
    label: string,
    subLabel: string | undefined,
    fitLabel: boolean = false,
): number {
    const ringClearance = r + SENTIMENT_RING_OUTER_OFFSET + COLLIDE_RING_MARGIN;
    if (!labelIsOutside(r, label, fitLabel)) return ringClearance;
    const pillClearance = r + PILL_GAP + PILL_HEIGHT;
    return Math.max(ringClearance, pillClearance);
}

/**
 * Bubble label(s): font size scales with radius, and once the bubble is too
 * small to hold legible in-fill text the label drops into a floating pill
 * below the bubble (design_html `.pill`) — white-ish surface, hairline
 * border, dark text with the count inline muted. Never white text over the
 * canvas.
 */
export const BubbleLabel: React.FC<BubbleLabelProps> = ({
    r,
    label,
    subLabel,
    palette,
    fitLabel = false,
    pillHidden = false,
}) => {
    const labelFontSize = Math.max(9, Math.min(13, r / 3.6));
    const outside = labelIsOutside(r, label, fitLabel);

    if (outside && pillHidden) return null;

    if (outside) {
        const width = pillWidth(label, subLabel);
        return (
            <g data-testid="bubble-pill" transform={`translate(0, ${r + PILL_GAP + PILL_HEIGHT / 2})`} style={{pointerEvents: 'none'}}>
                <rect
                    x={-width / 2}
                    y={-PILL_HEIGHT / 2}
                    width={width}
                    height={PILL_HEIGHT}
                    rx={PILL_HEIGHT / 2}
                    fill={hexToRgba(palette.chrome.surface, PILL_BG_OPACITY)}
                    stroke={palette.chrome.gridline}
                    strokeWidth={1}
                />
                <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontFamily="'Plus Jakarta Sans', Inter, sans-serif"
                    fontSize={PILL_FONT_SIZE}
                >
                    <tspan fontWeight={600} fill={palette.chrome.ink}>
                        {label}
                    </tspan>
                    {subLabel != null && (
                        <tspan dx={PILL_LABEL_GAP} fontWeight={500} fill={palette.chrome.inkMuted}>
                            {subLabel}
                        </tspan>
                    )}
                </text>
            </g>
        );
    }

    return (
        <>
            <text
                y={-2}
                textAnchor="middle"
                fontFamily="'Plus Jakarta Sans', Inter, sans-serif"
                fontWeight={800}
                fontSize={labelFontSize}
                fill="#fff"
                stroke="rgba(0,0,0,.25)"
                strokeWidth={2}
                style={{pointerEvents: 'none', paintOrder: 'stroke'}}
            >
                {label}
            </text>
            {subLabel != null && (
                <text
                    y={13}
                    textAnchor="middle"
                    fontFamily="'Plus Jakarta Sans', Inter, sans-serif"
                    fontWeight={500}
                    fontStyle="italic"
                    fontSize={Math.max(8, Math.min(labelFontSize, r / 4.6))}
                    fill="rgba(255,255,255,.88)"
                    style={{pointerEvents: 'none'}}
                >
                    {subLabel}
                </text>
            )}
        </>
    );
};

/**
 * SVG path for one annulus segment (donut slice) between innerR/outerR and
 * startAngle/endAngle (radians, 0 = top, increasing clockwise) — d3-arc's
 * angle convention without the d3-shape import (see module docblock).
 */
function annulusSegmentPath(
    innerR: number,
    outerR: number,
    startAngle: number,
    endAngle: number,
): string {
    const point = (r: number, a: number): [number, number] => [r * Math.sin(a), -r * Math.cos(a)];
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const [ox0, oy0] = point(outerR, startAngle);
    const [ox1, oy1] = point(outerR, endAngle);
    const [ix1, iy1] = point(innerR, endAngle);
    const [ix0, iy0] = point(innerR, startAngle);
    return [
        `M ${ox0} ${oy0}`,
        `A ${outerR} ${outerR} 0 ${largeArc} 1 ${ox1} ${oy1}`,
        `L ${ix1} ${iy1}`,
        `A ${innerR} ${innerR} 0 ${largeArc} 0 ${ix0} ${iy0}`,
        'Z',
    ].join(' ');
}

export interface BubbleSentimentRingProps {
    r: number;
    breakdown: BubbleSentimentBreakdown;
    /**
     * safe-green positive / muted-gray neutral / risk-red negative — pass
     * palette.status.{good,neutral,critical}. Never the accent purple: that
     * colour stays reserved for interactive state, not sentiment.
     */
    colors: BubbleSentimentColors;
}

/**
 * Segmented sentiment ring hugging the bubble edge (inner r+3.5, outer r+7,
 * 0.03rad gaps between slices) — NOT a flat pie fill. One <path> per
 * non-zero slice, in positive/neutral/negative order.
 */
export const BubbleSentimentRing: React.FC<BubbleSentimentRingProps> = ({r, breakdown, colors}) => {
    const slices: Array<{key: keyof BubbleSentimentBreakdown; frac: number; color: string}> = [
        {key: 'positive', frac: breakdown.positive, color: colors.positive},
        {key: 'neutral', frac: breakdown.neutral, color: colors.neutral},
        {key: 'negative', frac: breakdown.negative, color: colors.negative},
    ];
    let angle = 0;
    return (
        <g data-testid="bubble-sentiment-ring">
            {slices.map((slice) => {
                const start = angle;
                const end = angle + slice.frac * Math.PI * 2;
                angle = end;
                if (slice.frac <= 0) return null;
                const d = annulusSegmentPath(
                    r + SENTIMENT_RING_INNER_OFFSET,
                    r + SENTIMENT_RING_OUTER_OFFSET,
                    start + SENTIMENT_GAP_RAD,
                    end - SENTIMENT_GAP_RAD,
                );
                return (
                    <path
                        key={slice.key}
                        data-testid={`bubble-sentiment-arc-${slice.key}`}
                        d={d}
                        fill={slice.color}
                    />
                );
            })}
        </g>
    );
};
